import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import GlobalStyles from './styles/GlobalStyles';
import { ThemeProvider } from 'styled-components';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18n';
// Import Firebase Analytics
import { trackPageView } from './firebase/firebase';
// Import CookieConsent component
import CookieConsent from './components/CookieConsent';

// Lazy load các component trực tiếp sử dụng React.lazy
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));

// ScrollToTop component đã được tối ưu
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });

    // Apply active class to reveal elements that are visible
    const revealElements = document.querySelectorAll('.reveal');
    const windowHeight = window.innerHeight;

    requestAnimationFrame(() => {
      revealElements.forEach((element) => {
        const elementPosition = element.getBoundingClientRect().top;
        const elementVisible = 150;

        if (elementPosition < windowHeight - elementVisible) {
          element.classList.add('active');
        }
      });
    });

    // Track page view when route changes
    trackPageView(document.title, pathname);
  }, [pathname]);

  return null;
};

// Cải thiện loading spinner
const LoadingSpinner = React.memo(() => (
  <div className="loading-spinner" style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
    fontSize: '1.5rem',
    color: '#428DE7'
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem'
    }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '5px solid rgba(66, 141, 231, 0.2)',
        borderTop: '5px solid #428DE7',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <p>Loading content...</p>
    </div>
  </div>
));

// Tối ưu prefetch
const PrefetchOnView = React.memo(() => {
  const location = useLocation();

  useEffect(() => {
    // Sử dụng requestIdleCallback cho prefetch nếu có thể
    const idleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

    // Prefetch neighboring routes khi trên một trang cụ thể
    if (location.pathname === '/') {
      idleCallback(() => {
        import('./pages/Products');
        import('./pages/About');
      });
    } else if (location.pathname === '/products') {
      idleCallback(() => {
        import('./pages/ProductDetail');
        import('./pages/Home');
      });
    }
  }, [location]);

  return null;
});

// Analytics tracker component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views with pathname as page class
    const pageName = document.title;
    const pageClass = location.pathname.replace('/', '') || 'home';

    trackPageView(pageName, pageClass);

    // console log in dev mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`Analytics: Tracked page view - ${pageName} (${pageClass})`);
    }
  }, [location]);

  return null;
};

const theme = {
  primaryColor: '#428DE7',
  secondaryColor: '#2C3E50',
  textColor: '#333333',
  backgroundColor: '#F7FAFD',
};

function App() {
  // Tối ưu hiệu ứng scroll reveal
  useEffect(() => {
    const revealElements = () => {
      const elements = document.querySelectorAll('.reveal');
      const windowHeight = window.innerHeight;

      requestAnimationFrame(() => {
        elements.forEach((element) => {
          const elementPosition = element.getBoundingClientRect().top;
          const elementVisible = 150;

          if (elementPosition < windowHeight - elementVisible) {
            element.classList.add('active');
          } else {
            element.classList.remove('active');
          }
        });
      });
    };

    // Sử dụng passive listener để cải thiện hiệu suất cuộn
    window.addEventListener('scroll', revealElements, { passive: true });

    // Call once on page load
    revealElements();

    return () => {
      window.removeEventListener('scroll', revealElements);
    };
  }, []);

  // Xử lý thay đổi consent
  const handleConsentChange = (consentData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Consent updated:', consentData);
    }
  };

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <BrowserRouter>
          <div className="app">
            <Header />
            <main className="main-content">
              <ScrollToTop />
              <AnalyticsTracker />
              <Suspense fallback={<LoadingSpinner />}>
                <PrefetchOnView />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogDetail />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <CookieConsent onConsentChange={handleConsentChange} />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;
